import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { TRANSLATIONS, LANGUAGES, DEFAULT_LANG } from './translations';
import { translateColor } from './colorNames';

// Nyelvi kontextus. A választott nyelv localStorage-ban marad meg,
// és a <html lang> attribútumot is beállítja (SEO + képernyőolvasók).

const LanguageContext = createContext({ lang: DEFAULT_LANG, setLang: () => {}, t: (k) => k });

const STORAGE_KEY = 'ms_lang';

const detectInitial = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && TRANSLATIONS[saved]) return saved;
  } catch (e) {}
  try {
    const nav = (navigator.language || '').slice(0, 2).toLowerCase();
    if (TRANSLATIONS[nav]) return nav;
  } catch (e) {}
  return DEFAULT_LANG;
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectInitial);
  // Termékszöveg-szótár: { [productId]: { n: név, d: leírás } } — csak nem-magyar
  // nyelvnél töltődik be, dinamikus importtal (külön chunk, a fő bundle nem nő)
  const [prodDict, setProdDict] = useState(null);

  useEffect(() => {
    document.documentElement.lang = lang;
    try { localStorage.setItem(STORAGE_KEY, lang); } catch (e) {}
  }, [lang]);

  useEffect(() => {
    let cancelled = false;
    if (lang === DEFAULT_LANG) { setProdDict(null); return; }
    import(`../data/productI18n/${lang}.json`)
      .then(m => { if (!cancelled) setProdDict(m.default || m); })
      .catch(() => { if (!cancelled) setProdDict(null); });
    return () => { cancelled = true; };
  }, [lang]);

  const setLang = useCallback((next) => {
    if (TRANSLATIONS[next]) setLangState(next);
  }, []);

  // Fordítás: ha az adott nyelven nincs kulcs, magyarra esik vissza,
  // végső esetben magát a kulcsot adja (így sosem lesz üres felirat)
  const t = useCallback((key) => {
    const dict = TRANSLATIONS[lang] || {};
    if (dict[key] != null) return dict[key];
    const fallback = TRANSLATIONS[DEFAULT_LANG] || {};
    return fallback[key] != null ? fallback[key] : key;
  }, [lang]);

  // Termék-lokalizálás: lokalizált name/description-nel adja vissza a terméket.
  // Magyarnál (vagy hiányzó fordításnál) az eredetit adja — sosem lesz üres.
  const tp = useCallback((product) => {
    if (!product || lang === DEFAULT_LANG || !prodDict) return product;
    const tr = prodDict[product.id];
    if (!tr) return product;
    return { ...product, name: tr.n || product.name, description: tr.d || product.description };
  }, [lang, prodDict]);

  // Kategória-/alkategórianév: t('cat.<id>') ill. t('sub.<id>') kulccsal,
  // ha nincs fordítás, a magyar name marad
  const tcat = useCallback((catOrSub, kind = 'cat') => {
    if (!catOrSub) return '';
    const key = `${kind}.${catOrSub.id}`;
    const v = t(key);
    return v === key ? catOrSub.name : v;
  }, [t]);

  // Színnév-lokalizálás (token-alapú szótárral, ld. colorNames.js)
  const tcolor = useCallback((name) => translateColor(name, lang), [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, tp, tcat, tcolor, languages: LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
